import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Copy,
  Crown,
  Gift,
  Loader2,
  QrCode,
  RefreshCw,
  X,
  TrendingUp,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  createPlanOrder,
  createSepayOrder,
  getSepayOrderStatus,
  getMonthlyTopupProgress,
  getQuickTopupOffer,
  getTopupBonusCampaign,
  getStoredReferral,
  redeemVoucher,
  validateVoucher,
  type SepayOrder,
  type MonthlyTopupProgress,
  type QuickTopupOffer,
  type TopupBonusCampaign,
  type SubscriptionPlan,
  type VoucherValidation,
} from "@/lib/boide-api";
import { useBoideUser } from "@/hooks/use-boide-user";
import {
  isQuickTopupOfferError,
  isQuickTopupOfferApplicable,
  isQuickTopupOfferUsable,
  stampQuickTopupOffer,
} from "@/lib/quick-topup";
import { errorName, trackEvent } from "@/lib/analytics";
import { MonthlyTopupProgressCard } from "@/components/monthly-topup-progress";
import { QrSingleUseWarning } from "@/components/qr-single-use-warning";
import { PlanGrid } from "@/components/subscription/plan-grid";
import { planLabelOf } from "@/components/subscription/quota-row";
import { QUOTA_STATUS_QUERY_KEY, useQuotaStatus } from "@/hooks/use-quota-status";

export const Route = createFileRoute("/_authenticated/thanh-toan")({
  head: () => ({
    meta: [
      { title: "Nạp Tiền - Thanh Toán Qua Mã QR Ngân Hàng" },
      {
        name: "description",
        content:
          "Nạp tiền vào số dư Bói Đê qua mã QR ngân hàng nhanh chóng để gieo quẻ và lập lá số. Nạp ngay!",
      },
    ],
  }),
  component: PaymentPage,
});

const MIN_AMOUNT = 20_000;
const POLL_INTERVAL_MS = 4_000;
const DEFAULT_AMOUNT = 50_000;
const AMOUNT_PRESETS = [50_000, 100_000, 20_000, 1_000_000];
const PAID_STATUSES = new Set(["paid", "success", "completed", "complete"]);

function formatMoney(amount: number): string {
  return amount.toLocaleString("vi-VN");
}

function isPaidStatus(status: string): boolean {
  return PAID_STATUSES.has(status.trim().toLowerCase());
}

function PaymentPage() {
  const { refresh } = useBoideUser();
  const queryClient = useQueryClient();
  // Gói quota ngày — bán ngay trên trang này, dùng CHUNG khối QR bên dưới.
  const quota = useQuotaStatus();
  const quotaPlan = planLabelOf(quota.data?.subscriptions ?? []);
  const [planLoading, setPlanLoading] = useState(false);
  // 🔴 Đơn hiện tại có phải đơn MUA GÓI hay không, giữ trong ref chứ không suy từ
  // `order`: `GET /orders/{code}/status` không trả `plan`, và lượt poll chạy trong
  // một callback được memo hóa nên đọc `order` ở đó là đọc bản cũ. Ref quyết định
  // đúng hai chuyện: câu toast lúc trả tiền xong, và có invalidate quota hay không.
  const planOrderRef = useRef(false);
  const [amount, setAmount] = useState(DEFAULT_AMOUNT);
  const [customAmount, setCustomAmount] = useState(String(DEFAULT_AMOUNT));
  const [order, setOrder] = useState<SepayOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const paidToastShownRef = useRef(false);
  const orderSectionRef = useRef<HTMLElement>(null);
  const monthlyRateRef = useRef(0);
  const [monthlyProgress, setMonthlyProgress] = useState<MonthlyTopupProgress | null>(null);
  const [monthlyProgressLoading, setMonthlyProgressLoading] = useState(true);
  const [monthlyProgressError, setMonthlyProgressError] = useState("");
  const [quickOffer, setQuickOffer] = useState<QuickTopupOffer | null>(null);
  const [bonusCampaign, setBonusCampaign] = useState<TopupBonusCampaign | null>(null);
  const [bonusCampaignError, setBonusCampaignError] = useState("");

  const [voucherInput, setVoucherInput] = useState("");
  const [referralInput, setReferralInput] = useState(() => getStoredReferral() ?? "");
  const [voucher, setVoucher] = useState<VoucherValidation | null>(null);
  const [appliedCode, setAppliedCode] = useState("");
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherError, setVoucherError] = useState("");

  const [giftInput, setGiftInput] = useState("");
  const [giftLoading, setGiftLoading] = useState(false);
  const [giftError, setGiftError] = useState("");
  const [giftSuccess, setGiftSuccess] = useState("");

  const qrUrl = order?.qr_url || order?.url_qr || "";
  const isPaid = order ? isPaidStatus(order.status) : false;
  const amountInvalid = !Number.isFinite(amount) || amount < MIN_AMOUNT;
  const estimatedAmountToPay = voucher?.amount_to_pay ?? amount;
  const monthlyBonus = Math.round(
    Math.max(0, estimatedAmountToPay) * (monthlyProgress?.unlocked_rate ?? 0),
  );
  const voucherBonus = voucher?.benefit_amount ?? 0;
  const campaignBonus = useCallback(
    (value: number) => {
      if (!bonusCampaign?.active) return 0;
      return bonusCampaign.tiers
        .filter((tier) => tier.is_active !== false && tier.threshold_amount <= value)
        .reduce((best, tier) => Math.max(best, tier.bonus_amount), 0);
    },
    [bonusCampaign],
  );
  const fixedBonus = campaignBonus(estimatedAmountToPay);
  const quickBonus = isQuickTopupOfferApplicable(quickOffer, estimatedAmountToPay)
    ? Math.round(estimatedAmountToPay * (quickOffer.bonus_rate ?? 0))
    : 0;
  const estimatedBonus = Math.max(monthlyBonus, voucherBonus, fixedBonus, quickBonus);
  const estimatedCredited = Math.max(0, estimatedAmountToPay) + estimatedBonus;
  const estimatedSource = (() => {
    if (voucherBonus === estimatedBonus && voucherBonus > 0) return "mã khuyến mãi";
    if (monthlyBonus === estimatedBonus && monthlyBonus > 0) return "ưu đãi mốc nạp";
    if (fixedBonus === estimatedBonus && fixedBonus > 0) return "thưởng gói nạp";
    if (quickBonus === estimatedBonus && quickBonus > 0) return "Quick Topup";
    return "thưởng nạp tiền";
  })();

  const loadMonthlyProgress = useCallback(async (showUnlockToast = false) => {
    setMonthlyProgressError("");
    try {
      const progress = await getMonthlyTopupProgress();
      const previousRate = monthlyRateRef.current;
      monthlyRateRef.current = progress.unlocked_rate;
      setMonthlyProgress(progress);
      if (showUnlockToast && progress.unlocked_rate > previousRate) {
        toast.success(
          `Đã mở khóa ưu đãi +${Math.round(progress.unlocked_rate * 100)}% cho lần nạp sau.`,
        );
      }
    } catch (err) {
      trackEvent("monthly_topup_progress_error", { error_name: errorName(err) });
      setMonthlyProgressError("Chưa tải được tiến độ mốc nạp tháng này. Vui lòng thử lại sau.");
    } finally {
      setMonthlyProgressLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMonthlyProgress();
    // Stamp mốc nhận offer để tính lệch giờ client/server khi kiểm hạn dùng
    void getQuickTopupOffer()
      .then((offer) => setQuickOffer(offer ? (stampQuickTopupOffer(offer) ?? offer) : null))
      .catch(() => setQuickOffer(null));
    void getTopupBonusCampaign()
      .then((campaign) => {
        setBonusCampaign(campaign);
        setBonusCampaignError("");
      })
      .catch(() => {
        setBonusCampaign(null);
        setBonusCampaignError("Chưa tải được ưu đãi nạp tiền.");
      });
  }, [loadMonthlyProgress]);

  const statusLabel = useMemo(() => {
    if (!order) return "Chưa tạo giao dịch";
    if (isPaid) return "Đã thanh toán";
    return order.status || "Đang chờ thanh toán";
  }, [isPaid, order]);

  const updateOrderStatus = useCallback(
    async (code: string, silent = false) => {
      setChecking(true);
      try {
        const nextOrder = await getSepayOrderStatus(code);
        setOrder((prev) => ({ ...(prev ?? nextOrder), ...nextOrder }));
        if (isPaidStatus(nextOrder.status)) {
          trackEvent("payment_success", { amount: nextOrder.amount || 0 });
          void refresh();
          void loadMonthlyProgress(true);
          if (planOrderRef.current) {
            // Gói vừa kích hoạt: quota hôm nay đã khác. Dùng chung khóa với thẻ ví
            // trang chủ và navbar nên một lần invalidate là mọi chỗ đọc lại.
            void queryClient.invalidateQueries({ queryKey: QUOTA_STATUS_QUERY_KEY });
          }
          if (!paidToastShownRef.current) {
            paidToastShownRef.current = true;
            toast.success(
              planOrderRef.current
                ? "Thanh toán thành công. Gói đã được kích hoạt."
                : nextOrder.credited_amount
                  ? `Thanh toán thành công. Đã cộng ${formatMoney(nextOrder.credited_amount)}đ vào số dư.`
                  : "Thanh toán thành công. Số dư đã được cập nhật.",
            );
          }
        } else if (!silent) {
          toast.info("Giao dịch chưa được ghi nhận.");
        }
      } catch (err) {
        trackEvent("payment_status_error", { error_name: errorName(err), silent });
        const message = err instanceof Error ? err.message : "Không kiểm tra được giao dịch";
        if (!silent) toast.error(message);
      } finally {
        setChecking(false);
      }
    },
    [loadMonthlyProgress, queryClient, refresh],
  );

  useEffect(() => {
    if (!order?.code || isPaid) return;
    const timer = window.setInterval(() => {
      void updateOrderStatus(order.code, true);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [isPaid, order?.code, updateOrderStatus]);

  const clearVoucher = () => {
    setVoucher(null);
    setAppliedCode("");
    setVoucherError("");
  };

  const applyVoucher = async () => {
    const code = voucherInput.trim().toUpperCase();
    if (!code) return;
    if (amountInvalid) {
      setVoucherError(`Chọn số tiền trước khi áp mã.`);
      return;
    }
    trackEvent("payment_voucher_apply_start", { code });
    setVoucherLoading(true);
    setVoucherError("");
    try {
      const result = await validateVoucher(code, amount);
      if (!result.valid) {
        trackEvent("payment_voucher_apply_error", { code, reason: result.message });
        setVoucherError(result.message || "Mã không hợp lệ.");
        clearVoucher();
        return;
      }
      trackEvent("payment_voucher_apply_success", { code, benefit_amount: result.benefit_amount });
      setVoucher(result);
      setAppliedCode(code);
    } catch (err) {
      trackEvent("payment_voucher_apply_error", { code, error_name: errorName(err) });
      setVoucherError(err instanceof Error ? err.message : "Không kiểm tra được mã.");
      clearVoucher();
    } finally {
      setVoucherLoading(false);
    }
  };

  const handleVoucherKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void applyVoucher();
    }
  };

  const redeemGiftcode = async () => {
    const code = giftInput.trim().toUpperCase();
    if (!code) return;
    trackEvent("giftcode_redeem_start", { code });
    setGiftLoading(true);
    setGiftError("");
    setGiftSuccess("");
    try {
      const result = await redeemVoucher(code);
      const credited = result.credited_amount ?? 0;
      trackEvent("giftcode_redeem_success", {
        code,
        credited_amount: credited,
        new_balance: result.new_balance,
      });
      setGiftSuccess(
        credited > 0
          ? `${result.message || "Đổi giftcode thành công"} — đã cộng ${formatMoney(credited)}đ.`
          : result.message || "Đổi giftcode thành công.",
      );
      setGiftInput("");
      toast.success(
        credited > 0
          ? `Đã nhận ${formatMoney(credited)}đ từ giftcode.`
          : "Đổi giftcode thành công.",
      );
      void refresh();
    } catch (err) {
      trackEvent("giftcode_redeem_error", { code, error_name: errorName(err) });
      setGiftError(err instanceof Error ? err.message : "Không đổi được giftcode.");
    } finally {
      setGiftLoading(false);
    }
  };

  const handleGiftKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void redeemGiftcode();
    }
  };

  const selectAmount = (nextAmount: number) => {
    trackEvent("payment_amount_select", { amount: nextAmount });
    setAmount(nextAmount);
    setCustomAmount(String(nextAmount));
    setError("");
    clearVoucher();
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    const parsedAmount = Number(value.replace(/\D/g, ""));
    setAmount(parsedAmount);
    setError("");
    clearVoucher();
  };

  /**
   * Mua gói: cùng một luồng đơn SePay, chỉ khác là KHÔNG gửi `amount` (giá lấy từ
   * DB) và không kèm voucher / mã giới thiệu / Quick Topup — BE bỏ qua hết khi đơn
   * có `plan_slug`, nên gửi lên chỉ làm người dùng tưởng ưu đãi được áp.
   */
  const buyPlan = async (plan: SubscriptionPlan) => {
    setPlanLoading(true);
    setError("");
    paidToastShownRef.current = false;
    trackEvent("plan_order_create_start", { plan_slug: plan.slug });
    try {
      const nextOrder = await createPlanOrder(plan.slug);
      planOrderRef.current = true;
      setOrder(nextOrder);
      trackEvent("plan_order_create_success", {
        plan_slug: plan.slug,
        amount: nextOrder.amount || plan.price,
      });
      window.setTimeout(() => {
        orderSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      toast.success(`Đã tạo giao dịch mua ${plan.name}.`);
    } catch (err) {
      trackEvent("plan_order_create_error", { plan_slug: plan.slug, error_name: errorName(err) });
      const message = err instanceof Error ? err.message : "Không tạo được giao dịch mua gói";
      setError(message);
      toast.error(message);
    } finally {
      setPlanLoading(false);
    }
  };

  const createOrder = async (event: React.FormEvent) => {
    event.preventDefault();
    if (amountInvalid) {
      setError(`Số tiền tối thiểu là ${formatMoney(MIN_AMOUNT)}đ.`);
      return;
    }

    setLoading(true);
    setError("");
    paidToastShownRef.current = false;
    // Đơn nạp ví: hạ cờ gói. Thiếu dòng này thì tạo đơn nạp ngay sau một đơn mua
    // gói sẽ báo "Gói đã được kích hoạt" khi trả tiền xong.
    planOrderRef.current = false;
    trackEvent("payment_order_create_start", { amount, voucher_code: appliedCode || undefined });
    const quickTopupOfferId = isQuickTopupOfferApplicable(quickOffer, estimatedAmountToPay)
      ? quickOffer.id
      : undefined;
    try {
      let nextOrder: SepayOrder;
      let offerRejected = false;
      try {
        nextOrder = await createSepayOrder(
          amount,
          appliedCode || undefined,
          referralInput.trim() || undefined,
          quickTopupOfferId,
        );
      } catch (err) {
        // Offer stale (đã dùng/hết hạn giữa chừng, race giữa 2 tab...) —
        // bỏ ưu đãi và tạo lại đơn để user luôn nạp được.
        if (!quickTopupOfferId || !isQuickTopupOfferError(err)) throw err;
        offerRejected = true;
        trackEvent("payment_quick_topup_offer_stale", { offer_id: quickTopupOfferId });
        setQuickOffer(null);
        void getQuickTopupOffer()
          .then((offer) => setQuickOffer(offer ? (stampQuickTopupOffer(offer) ?? offer) : null))
          .catch(() => setQuickOffer(null));
        nextOrder = await createSepayOrder(
          amount,
          appliedCode || undefined,
          referralInput.trim() || undefined,
        );
      }
      trackEvent("payment_order_create_success", { amount: nextOrder.amount || amount });
      setOrder(nextOrder);
      if (offerRejected) {
        toast.warning(
          "Ưu đãi Quick Topup đã hết hạn hoặc đã dùng — giao dịch được tạo không kèm ưu đãi này.",
        );
      }
      window.setTimeout(() => {
        orderSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      toast.success("Đã tạo giao dịch nạp tiền.");
    } catch (err) {
      trackEvent("payment_order_create_error", { amount, error_name: errorName(err) });
      const message = err instanceof Error ? err.message : "Không tạo được giao dịch";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text: string, label: string) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    trackEvent("payment_copy_field", { field: label });
    toast.success(`Đã copy ${label}.`);
  };

  const resetOrder = () => {
    trackEvent("payment_order_reset");
    setOrder(null);
    setError("");
    paidToastShownRef.current = false;
    planOrderRef.current = false;
    clearVoucher();
    setVoucherInput("");
  };

  return (
    <main className="min-h-0 flex-1 overflow-y-auto bg-[#fbfaf8] px-4 py-5 pb-24 text-[#211d18] md:px-8 md:py-10 md:pb-10 dark:bg-background dark:text-foreground">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-5 md:gap-6">
        <section className="order-1 rounded-[28px] border border-[#e6dfd5] bg-white p-5 shadow-[0_10px_35px_rgba(71,53,29,0.06)] md:p-8 dark:border-border dark:bg-card">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="font-serif-soft text-3xl font-semibold tracking-tight md:text-4xl">
                Chọn gói nạp tiền
              </h1>
              <p className="mt-2 text-sm text-[#817970] dark:text-muted-foreground">
                Chọn gói phù hợp với nhu cầu của bạn
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#817970] dark:text-muted-foreground">
              <ShieldCheck className="h-5 w-5" />
              Thanh toán bảo mật
            </div>
            {order && (
              <div className="rounded-2xl border border-gold-soft/30 bg-background/40 px-4 py-3 text-sm">
                <div className="text-muted-foreground">Trạng thái</div>
                <div
                  className={
                    isPaid ? "font-semibold text-emerald-400" : "font-semibold text-primary"
                  }
                >
                  {statusLabel}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ── Gói quota ngày ────────────────────────────────────────────────
            Đặt TRƯỚC ô chọn số tiền: với người xem thường xuyên thì gói rẻ hơn
            nạp lẻ, và đây là chỗ duy nhất họ đang cầm sẵn ý định trả tiền.
            Đơn mua gói dùng CHUNG khối QR bên dưới (`order-4`) — dựng một khối
            thanh toán thứ hai trên cùng trang là hai vòng poll cùng chạy và hai
            câu toast cùng bắn. */}
        <section className="order-2 rounded-[28px] border border-[#e6dfd5] bg-white p-5 shadow-[0_10px_35px_rgba(71,53,29,0.06)] md:p-7 dark:border-border dark:bg-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="flex items-center gap-2 font-serif-soft text-2xl font-semibold">
                <Crown className="h-5 w-5 shrink-0 text-primary" />
                Gói quota ngày
              </h2>
              <p className="mt-1 text-sm text-[#817970] dark:text-muted-foreground">
                Hạn mức tiêu <strong className="text-foreground">mỗi ngày</strong>, làm mới lúc 0h
                giờ Việt Nam và dùng trước tiền ví. Rẻ hơn nạp lẻ nếu bạn xem thường xuyên.
              </p>
            </div>
            <Link
              to="/goi"
              className="inline-flex shrink-0 items-center gap-1 rounded-full border border-gold-soft/50 px-3 py-1.5 text-xs font-semibold text-primary transition hover:border-primary/60"
            >
              Gói của tôi
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {/* Gói đang chạy: hiện hạn mức CÒN LẠI hôm nay, không phải mức của gói —
              người ta mở trang này lúc sắp hết tiền, nên con số cần biết là còn bao
              nhiêu. Chỉ hiện khi thật sự có gói (xem `QuotaRow`). */}
          {quota.data && quotaPlan.primary ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-primary/25 bg-primary/[0.06] px-4 py-3 text-sm">
              <span className="min-w-0">
                Đang dùng <strong className="text-foreground">{quotaPlan.primary.plan_name}</strong>
                {quotaPlan.extra > 0 ? (
                  <span className="text-primary"> +{quotaPlan.extra} gói</span>
                ) : null}{" "}
                · còn {quotaPlan.primary.days_left} ngày
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                Hạn mức hôm nay:{" "}
                <strong className="font-semibold text-primary">
                  {formatMoney(quota.data.remaining)}đ
                </strong>{" "}
                / {formatMoney(quota.data.quota_limit)}đ
              </span>
            </div>
          ) : null}

          <PlanGrid
            className="mt-4"
            ownedSlugs={new Set((quota.data?.subscriptions ?? []).map((sub) => sub.plan_slug))}
            buying={planLoading}
            onBuy={(plan) => void buyPlan(plan)}
          />

          <p className="mt-3 text-xs leading-5 text-[#817970] dark:text-muted-foreground">
            Mua thêm gói khi đang có gói cũng được — hạn mức mỗi ngày{" "}
            <strong className="text-foreground">cộng dồn</strong>. Tiền mua gói{" "}
            <strong className="text-foreground">không cộng vào ví</strong> và không áp voucher hay
            thưởng nạp.
          </p>
        </section>

        <div className="order-6">
          <MonthlyTopupProgressCard
            progress={monthlyProgress}
            loading={monthlyProgressLoading}
            error={monthlyProgressError}
          />
        </div>

        <section className="order-5 rounded-[24px] border border-[#e6dfd5] bg-white p-5 shadow-[0_10px_35px_rgba(71,53,29,0.05)] md:p-6 dark:border-border dark:bg-card">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gold-gradient text-primary-foreground shadow-gold">
                <Gift className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-serif-soft text-2xl">Đổi giftcode</h2>
                <p className="text-sm text-muted-foreground">
                  Nhập mã quà tặng để cộng thẳng số dư, không cần chuyển khoản.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-2 sm:flex-row">
            <input
              type="text"
              value={giftInput}
              onChange={(e) => {
                setGiftInput(e.target.value.toUpperCase());
                setGiftError("");
                setGiftSuccess("");
              }}
              onKeyDown={handleGiftKeyDown}
              placeholder="VD: BOIYN6"
              className="min-w-0 flex-1 rounded-2xl border border-gold-soft/40 bg-background/40 px-4 py-3 text-sm uppercase outline-none transition focus:border-primary/70"
            />
            <button
              type="button"
              onClick={() => void redeemGiftcode()}
              disabled={giftLoading || !giftInput.trim()}
              className="flex shrink-0 items-center justify-center gap-2 rounded-full bg-gold-gradient px-6 py-3 text-sm font-medium text-primary-foreground shadow-gold transition hover:scale-[1.01] disabled:opacity-50"
            >
              {giftLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Gift className="h-4 w-4" />
              )}
              Đổi quà
            </button>
          </div>

          {giftSuccess && (
            <div className="mt-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm font-medium text-emerald-400">
              {giftSuccess}
            </div>
          )}
          {giftError && (
            <div className="mt-3 rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {giftError}
            </div>
          )}
        </section>

        <div className="order-3 grid gap-5 md:gap-6">
          <section className="rounded-[28px] border border-[#e6dfd5] bg-white p-5 shadow-[0_10px_35px_rgba(71,53,29,0.06)] md:p-7 dark:border-border dark:bg-card">
            <div className="mb-5 flex items-center justify-between gap-3">
              <h2 className="font-serif-soft text-2xl font-semibold">Chọn số tiền</h2>
              <span className="text-xs text-[#817970] dark:text-muted-foreground">
                Tối thiểu 20.000đ
              </span>
            </div>
            <form onSubmit={createOrder} className="mt-6 space-y-5">
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {AMOUNT_PRESETS.map((preset) => {
                  const selected = amount === preset;
                  const fixedBonusForPreset = campaignBonus(preset);
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => selectAmount(preset)}
                      className={`relative min-h-[142px] rounded-[22px] border px-4 py-4 text-left transition md:min-h-[154px] md:px-5 md:py-5 ${
                        selected
                          ? "border-[#8a5b0a] bg-[linear-gradient(145deg,#b97908,#d49a1d)] text-white shadow-[0_12px_25px_rgba(154,101,10,0.24)]"
                          : "border-[#e6dfd5] bg-[#fffdfa] hover:border-[#bf8620] dark:border-border dark:bg-background/40"
                      }`}
                    >
                      {preset === DEFAULT_AMOUNT && (
                        <span
                          className={`absolute -top-3 left-4 rounded-full px-3 py-1 text-[11px] font-semibold ${selected ? "bg-white text-[#a46a00]" : "bg-[#fff1c9] text-[#9a6200]"}`}
                        >
                          ☆ Khuyên dùng
                        </span>
                      )}
                      <div className="text-[25px] font-semibold tracking-tight md:text-[29px]">
                        {formatMoney(preset)}đ
                      </div>
                      <div
                        className={`mt-1 text-sm ${selected ? "text-white/85" : "text-[#817970] dark:text-muted-foreground"}`}
                      >
                        Nạp nhanh
                      </div>
                      <div
                        className={`mt-3 inline-flex rounded-full px-3 py-1.5 text-xs font-bold tracking-[0.01em] shadow-sm ${selected ? "bg-[#fff7dc] text-[#8a5800] shadow-[0_3px_10px_rgba(77,43,0,0.18)]" : fixedBonusForPreset > 0 ? "bg-[#eff8ec] text-[#2d7141]" : "bg-[#f5f2ed] text-[#817970] dark:bg-muted"}`}
                      >
                        {fixedBonusForPreset > 0
                          ? `Khuyến mãi ${formatMoney(fixedBonusForPreset)}đ`
                          : "Không có thưởng"}
                      </div>
                      <div
                        className={`mt-3 border-t pt-2 text-sm ${selected ? "border-white/20 text-white" : "border-[#ebe4d9] text-[#5e554e] dark:border-border dark:text-foreground"}`}
                      >
                        Nhận{" "}
                        <span className="font-semibold">
                          {formatMoney(preset + fixedBonusForPreset)}đ
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {!amountInvalid && (
                <div className="grid gap-3 rounded-[22px] border border-[#e6dfd5] bg-[#fcfaf7] p-5 sm:grid-cols-2 dark:border-border dark:bg-background/35">
                  <div>
                    <div className="text-xs text-[#817970] dark:text-muted-foreground">
                      Số tiền nạp
                    </div>
                    <div className="mt-1 font-serif-soft text-2xl font-semibold">
                      {formatMoney(estimatedAmountToPay)}đ
                    </div>
                  </div>
                  <div className="sm:text-right">
                    <div className="text-xs text-[#817970] dark:text-muted-foreground">
                      Số tiền nhận
                    </div>
                    <div className="mt-1 font-serif-soft text-2xl font-semibold text-primary">
                      {formatMoney(estimatedCredited)}đ
                    </div>
                    {estimatedBonus > 0 && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        + {formatMoney(estimatedBonus)}đ từ {estimatedSource}
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground sm:col-span-2">
                    Ước tính theo ưu đãi hiện tại; quyền lợi cuối cùng được chốt khi tiền về.
                  </p>
                  {isQuickTopupOfferUsable(quickOffer) && (
                    <p className="rounded-xl border border-amber-400/40 bg-amber-400/10 px-3 py-2 text-xs text-amber-800 sm:col-span-2 dark:text-amber-200">
                      Ưu đãi Quick Topup chỉ áp dụng cho khoản nạp từ{" "}
                      <strong>{formatMoney(quickOffer.min_amount)}đ</strong> đến{" "}
                      <strong>{formatMoney(quickOffer.max_amount)}đ</strong>.
                    </p>
                  )}
                </div>
              )}
              {bonusCampaignError && <p className="text-xs text-[#9a6200]">{bonusCampaignError}</p>}

              <label className="block">
                <span className="mb-2 block font-display text-[10px] tracking-[0.3em] text-primary">
                  SỐ TIỀN KHÁC
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={customAmount}
                  onChange={(event) => handleCustomAmount(event.target.value)}
                  className="w-full rounded-2xl border border-gold-soft/40 bg-background/40 px-4 py-3 text-sm outline-none transition focus:border-primary/70"
                  placeholder="Nhập số tiền"
                />
              </label>

              <div className="space-y-2">
                <span className="block font-display text-[10px] tracking-[0.3em] text-primary">
                  MÃ GIỚI THIỆU
                </span>
                <input
                  type="text"
                  value={referralInput}
                  onChange={(e) => setReferralInput(e.target.value.trim().toLowerCase())}
                  placeholder="Nhập mã giới thiệu (nếu có) để nhận khuyến mãi"
                  className="w-full rounded-2xl border border-gold-soft/40 bg-background/40 px-4 py-3 text-sm outline-none transition focus:border-primary/70"
                />
              </div>

              <div className="space-y-2">
                <span className="block font-display text-[10px] tracking-[0.3em] text-primary">
                  MÃ KHUYẾN MÃI
                </span>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={voucherInput}
                    onChange={(e) => {
                      setVoucherInput(e.target.value.toUpperCase());
                      if (voucher) clearVoucher();
                      setVoucherError("");
                    }}
                    onKeyDown={handleVoucherKeyDown}
                    disabled={!!voucher}
                    placeholder="VD: MLYNN6"
                    className="min-w-0 flex-1 rounded-2xl border border-gold-soft/40 bg-background/40 px-4 py-3 text-sm uppercase outline-none transition focus:border-primary/70 disabled:opacity-60"
                  />
                  {voucher ? (
                    <button
                      type="button"
                      onClick={() => {
                        trackEvent("payment_voucher_clear", { code: appliedCode });
                        clearVoucher();
                        setVoucherInput("");
                      }}
                      className="flex shrink-0 items-center justify-center gap-1.5 rounded-full border border-gold-soft/40 bg-background/40 px-4 py-3 text-sm transition hover:border-destructive/60 hover:text-destructive"
                    >
                      <X className="h-3.5 w-3.5" />
                      Xóa
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void applyVoucher()}
                      disabled={voucherLoading || !voucherInput.trim()}
                      className="flex shrink-0 items-center justify-center gap-1.5 rounded-full border border-gold-soft/50 bg-background/40 px-4 py-3 text-sm transition hover:border-primary/60 disabled:opacity-50"
                    >
                      {voucherLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      Nhận lộc
                    </button>
                  )}
                </div>

                {voucher && (
                  <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm">
                    <div className="font-medium text-emerald-400">{voucher.message}</div>
                    <div className="mt-1 text-muted-foreground">
                      Nạp{" "}
                      <span className="font-semibold text-foreground">
                        {formatMoney(voucher.amount_to_pay)}đ
                      </span>{" "}
                      → Nhận{" "}
                      <span className="font-semibold text-emerald-400">
                        {formatMoney(voucher.amount_credited)}đ
                      </span>
                      {voucher.benefit_amount > 0 && (
                        <span className="ml-1 text-xs">
                          (+{formatMoney(voucher.benefit_amount)}đ bonus)
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {voucherError && (
                  <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                    {voucherError}
                  </div>
                )}
              </div>

              {error && (
                <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || amountInvalid}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-gold-gradient px-5 py-3 text-sm font-medium text-primary-foreground shadow-gold transition hover:scale-[1.01] disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <QrCode className="h-4 w-4" />
                )}
                {voucher
                  ? `Tạo mã QR nạp ${formatMoney(voucher.amount_to_pay)}đ → nhận ${formatMoney(voucher.amount_credited)}đ`
                  : `Tạo mã QR nạp ${formatMoney(amount || 0)}đ`}
              </button>

              <p className="mt-3 text-center text-xs text-muted-foreground">
                Tiền đã nạp không được hoàn lại hoặc rút ra. Khi nạp, bạn đồng ý với{" "}
                <Link to="/terms" target="_blank" className="text-primary underline">
                  Điều khoản dịch vụ
                </Link>
                .
              </p>
            </form>
          </section>

          <section
            ref={orderSectionRef}
            className="order-4 rounded-[28px] border border-[#e6dfd5] bg-white p-5 shadow-[0_10px_35px_rgba(71,53,29,0.06)] md:p-7 dark:border-border dark:bg-card"
          >
            {!order ? (
              <div className="flex min-h-[280px] md:min-h-[420px] flex-col items-center justify-center rounded-3xl border border-dashed border-gold-soft/35 bg-background/30 p-6 md:p-8 text-center">
                <QrCode className="h-14 w-14 text-primary/70" />
                <h2 className="mt-4 font-serif-soft text-2xl">Chưa có giao dịch</h2>
                <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                  Chọn một gói quota ngày, hoặc chọn số tiền nạp lẻ, rồi tạo mã QR.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-serif-soft text-2xl">Thông tin chuyển khoản</h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Quét QR, hoặc chuyển khoản đúng số tiền và giữ nguyên nội dung giao dịch.
                    </p>
                  </div>
                  {isPaid && <CheckCircle2 className="h-7 w-7 shrink-0 text-emerald-400" />}
                </div>

                {/* Đơn mua gói phải tự khai ra: mọi đơn khác trên trang này đều cộng
                    ví, nên không nói thì người dùng sẽ đi tìm số dư tăng và tưởng
                    thanh toán lỗi. `order.plan` chỉ có ở phản hồi TẠO đơn — lượt poll
                    merge lên bản đó nên nó không bị mất. */}
                {order.plan ? (
                  <div className="rounded-2xl border border-primary/25 bg-primary/[0.06] px-4 py-3 text-sm">
                    <p className="flex items-center gap-2 font-semibold text-foreground">
                      <Crown className="h-4 w-4 shrink-0 text-primary" />
                      {order.plan.name}
                    </p>
                    <p className="mt-1 text-muted-foreground">
                      {formatMoney(order.plan.daily_quota)}đ/ngày trong {order.plan.duration_days}{" "}
                      ngày. Tiền mua gói{" "}
                      <strong className="text-foreground">không cộng vào ví</strong> — gói cấp hạn
                      mức tiêu mỗi ngày, làm mới lúc 0h giờ Việt Nam.
                    </p>
                  </div>
                ) : null}

                <QrSingleUseWarning />

                <div className="grid gap-5 md:grid-cols-[minmax(260px,340px)_1fr]">
                  <div className="flex aspect-square w-full items-center justify-center rounded-3xl border border-gold-soft/30 bg-white p-3 md:max-w-[340px] md:p-4">
                    {qrUrl ? (
                      <img
                        src={qrUrl}
                        alt="QR thanh toán"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <div className="text-center text-sm text-slate-500">Không có QR</div>
                    )}
                  </div>

                  <div className="space-y-3 text-sm">
                    <PaymentInfo label="Số tiền" value={`${formatMoney(order.amount)}đ`} />
                    <PaymentInfo
                      label="Mã giao dịch — nội dung chuyển khoản (giữ nguyên)"
                      value={order.code}
                      copyLabel="mã giao dịch"
                      onCopy={copyText}
                    />
                    <PaymentInfo label="Ngân hàng" value={order.bank || "--"} />
                    <PaymentInfo
                      label="Tài khoản"
                      value={order.account || "--"}
                      copyLabel="số tài khoản"
                      onCopy={copyText}
                    />
                    <PaymentInfo label="Trạng thái" value={statusLabel} />
                    {order.paid_at && (
                      <PaymentInfo label="Đã thanh toán lúc" value={order.paid_at} />
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => void updateOrderStatus(order.code)}
                    disabled={checking || isPaid}
                    className="flex flex-1 items-center justify-center gap-2 rounded-full border border-gold-soft/50 bg-background/40 px-5 py-3 text-sm font-medium transition hover:border-primary/60 disabled:opacity-50"
                  >
                    <RefreshCw className={`h-4 w-4 ${checking ? "animate-spin" : ""}`} />
                    Kiểm tra thanh toán
                  </button>
                  <button
                    type="button"
                    onClick={resetOrder}
                    className="flex flex-1 items-center justify-center rounded-full bg-gold-gradient px-5 py-3 text-sm font-medium text-primary-foreground shadow-gold transition hover:scale-[1.01]"
                  >
                    {isPaid ? "Xong" : "Tạo giao dịch mới"}
                  </button>
                </div>
              </div>
            )}
          </section>

          {/* Nút sang trang Biến động số dư (trang riêng) */}
          <Link
            to="/bien-dong-so-du"
            className="order-7 flex items-center justify-between rounded-[24px] border border-[#e6dfd5] bg-white p-5 shadow-[0_10px_35px_rgba(71,53,29,0.05)] transition hover:border-primary/60 md:p-6 dark:border-border dark:bg-card"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gold-gradient/15 text-primary">
                <TrendingUp className="h-5 w-5" />
              </div>
              <div>
                <div className="font-serif-soft text-lg text-foreground">Biến động số dư</div>
                <div className="text-xs text-muted-foreground">
                  Xem lịch sử nạp tiền & chi tiêu, số dư sau mỗi giao dịch
                </div>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </Link>
        </div>
      </div>
    </main>
  );
}

function PaymentInfo({
  label,
  value,
  copyLabel,
  onCopy,
}: {
  label: string;
  value: string;
  copyLabel?: string;
  onCopy?: (value: string, label: string) => Promise<void>;
}) {
  return (
    <div className="rounded-2xl border border-gold-soft/25 bg-background/35 px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 flex items-center justify-between gap-3">
        <span className="break-all font-medium text-foreground">{value}</span>
        {copyLabel && onCopy && value !== "--" && (
          <button
            type="button"
            onClick={() => void onCopy(value, copyLabel)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-gold-soft/40 text-muted-foreground transition hover:border-primary/60 hover:text-primary"
          >
            <Copy className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
